import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import { Key, Plus, Trash2, Copy, Check, Eye, EyeOff, Loader2, Shield, ChevronDown, Settings } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

interface EndpointPermission {
  read: boolean;
  write: boolean;
}

interface ApiPermissions {
  endpoints: {
    products: EndpointPermission;
    stock: EndpointPermission;
    orders: EndpointPermission;
    sync: EndpointPermission;
  };
  rateLimit?: number;
}

const defaultPermissions: ApiPermissions = {
  endpoints: {
    products: { read: true, write: false },
    stock: { read: true, write: false },
    orders: { read: false, write: false },
    sync: { read: false, write: false },
  },
  rateLimit: 1000,
};

interface ApiKey {
  id: string;
  name: string;
  api_key: string;
  created_at: string;
  last_used_at: string | null;
  is_active: boolean;
  expires_at: string | null;
  request_count: number;
  permissions: ApiPermissions | null;
}

interface NewApiKeyData {
  api_key: string;
  api_secret: string;
}

// Simple hash function for secrets (same as edge function)
async function hashSecret(secret: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(secret);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Generate random string
function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += chars[randomValues[i] % chars.length];
  }
  return result;
}

const endpointLabels: Record<string, { label: string; description: string }> = {
  products: { label: 'Producten', description: 'Toegang tot productinformatie en zoekfunctie' },
  stock: { label: 'Voorraad', description: 'Toegang tot voorraadniveaus en beschikbaarheid' },
  orders: { label: 'Orders', description: 'Toegang tot ordergegevens en status' },
  sync: { label: 'Synchronisatie', description: 'Kan synchronisatie-operaties uitvoeren' },
};

export default function ApiKeyManager() {
  const { user } = useAuth();
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showSecretDialog, setShowSecretDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [editingKey, setEditingKey] = useState<ApiKey | null>(null);
  const [editingPermissions, setEditingPermissions] = useState<ApiPermissions>(defaultPermissions);
  const [deleteKeyId, setDeleteKeyId] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<ApiPermissions>(defaultPermissions);
  const [newKeyData, setNewKeyData] = useState<NewApiKeyData | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('id, name, api_key, created_at, last_used_at, is_active, expires_at, request_count, permissions')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys((data || []).map(k => ({
        ...k,
        permissions: k.permissions as unknown as ApiPermissions | null
      })));
    } catch (error) {
      console.error('Error loading API keys:', error);
      toast.error('Kon API keys niet laden');
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Vul een naam in voor de API key');
      return;
    }

    setCreating(true);
    try {
      // Generate API key and secret
      const apiKey = `inv_${generateRandomString(32)}`;
      const apiSecret = generateRandomString(48);
      const apiSecretHash = await hashSecret(apiSecret);

      const { error } = await supabase
        .from('api_keys')
        .insert([{
          name: newKeyName.trim(),
          api_key: apiKey,
          api_secret_hash: apiSecretHash,
          created_by: user?.id,
          permissions: newKeyPermissions as unknown as null,
        }] as any);

      if (error) throw error;

      setNewKeyData({ api_key: apiKey, api_secret: apiSecret });
      setShowCreateDialog(false);
      setShowSecretDialog(true);
      setNewKeyName('');
      setNewKeyPermissions(defaultPermissions);
      loadApiKeys();
      toast.success('API key succesvol aangemaakt');
    } catch (error) {
      console.error('Error creating API key:', error);
      toast.error('Kon API key niet aanmaken');
    } finally {
      setCreating(false);
    }
  };

  const toggleApiKey = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from('api_keys')
        .update({ is_active: isActive })
        .eq('id', id);

      if (error) throw error;
      
      setApiKeys(keys => keys.map(k => k.id === id ? { ...k, is_active: isActive } : k));
      toast.success(isActive ? 'API key geactiveerd' : 'API key gedeactiveerd');
    } catch (error) {
      console.error('Error toggling API key:', error);
      toast.error('Kon API key status niet wijzigen');
    }
  };

  const updatePermissions = async () => {
    if (!editingKey) return;

    try {
      const { error } = await supabase
        .from('api_keys')
        .update({ permissions: editingPermissions as any })
        .eq('id', editingKey.id);

      if (error) throw error;
      
      setApiKeys(keys => keys.map(k => 
        k.id === editingKey.id ? { ...k, permissions: editingPermissions } : k
      ));
      setShowPermissionsDialog(false);
      setEditingKey(null);
      toast.success('Rechten bijgewerkt');
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error('Kon rechten niet bijwerken');
    }
  };

  const deleteApiKey = async () => {
    if (!deleteKeyId) return;

    try {
      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', deleteKeyId);

      if (error) throw error;
      
      setApiKeys(keys => keys.filter(k => k.id !== deleteKeyId));
      toast.success('API key verwijderd');
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Kon API key niet verwijderen');
    } finally {
      setDeleteKeyId(null);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Gekopieerd naar klembord');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const maskApiKey = (key: string) => {
    return key.substring(0, 8) + '...' + key.substring(key.length - 4);
  };

  const openPermissionsDialog = (key: ApiKey) => {
    setEditingKey(key);
    setEditingPermissions(key.permissions || defaultPermissions);
    setShowPermissionsDialog(true);
  };

  const toggleExpanded = (keyId: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(keyId)) {
        next.delete(keyId);
      } else {
        next.add(keyId);
      }
      return next;
    });
  };

  const getPermissionSummary = (permissions: ApiPermissions | null) => {
    if (!permissions) return 'Geen rechten';
    const { endpoints } = permissions;
    const active: string[] = [];
    
    Object.entries(endpoints).forEach(([key, perm]) => {
      const label = endpointLabels[key]?.label || key;
      if (perm.read && perm.write) {
        active.push(`${label} (R/W)`);
      } else if (perm.read) {
        active.push(`${label} (R)`);
      } else if (perm.write) {
        active.push(`${label} (W)`);
      }
    });
    
    return active.length > 0 ? active.join(', ') : 'Geen rechten';
  };

  const PermissionsEditor = ({ 
    permissions, 
    onChange 
  }: { 
    permissions: ApiPermissions; 
    onChange: (p: ApiPermissions) => void;
  }) => (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label className="text-sm font-medium">Endpoint Toegang</Label>
        <div className="rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 text-sm font-medium">Endpoint</th>
                <th className="text-center p-3 text-sm font-medium w-20">Lezen</th>
                <th className="text-center p-3 text-sm font-medium w-20">Schrijven</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(endpointLabels).map(([key, { label, description }]) => {
                const endpointKey = key as keyof typeof permissions.endpoints;
                const perm = permissions.endpoints[endpointKey];
                
                return (
                  <tr key={key} className="border-t">
                    <td className="p-3">
                      <div className="font-medium">{label}</div>
                      <div className="text-sm text-muted-foreground">{description}</div>
                    </td>
                    <td className="text-center p-3">
                      <Checkbox
                        checked={perm.read}
                        onCheckedChange={(checked) => {
                          const newEndpoints = { ...permissions.endpoints };
                          newEndpoints[endpointKey] = { ...perm, read: !!checked };
                          onChange({ ...permissions, endpoints: newEndpoints });
                        }}
                      />
                    </td>
                    <td className="text-center p-3">
                      <Checkbox
                        checked={perm.write}
                        onCheckedChange={(checked) => {
                          const newEndpoints = { ...permissions.endpoints };
                          newEndpoints[endpointKey] = { ...perm, write: !!checked };
                          onChange({ ...permissions, endpoints: newEndpoints });
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="rateLimit">Rate Limit (requests/uur)</Label>
        <Input
          id="rateLimit"
          type="number"
          min={100}
          max={10000}
          value={permissions.rateLimit || 1000}
          onChange={(e) => onChange({ ...permissions, rateLimit: parseInt(e.target.value) || 1000 })}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                API Keys
              </CardTitle>
              <CardDescription>
                Beheer API keys en rechten voor toegang tot de Inventory API
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Nieuwe API Key
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nog geen API keys aangemaakt</p>
              <p className="text-sm">Klik op "Nieuwe API Key" om te beginnen</p>
            </div>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((key) => (
                <Collapsible
                  key={key.id}
                  open={expandedKeys.has(key.id)}
                  onOpenChange={() => toggleExpanded(key.id)}
                >
                  <div className="border rounded-lg">
                    <div className="flex items-center justify-between p-4">
                      <div className="space-y-1 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{key.name}</span>
                          <Badge variant={key.is_active ? 'default' : 'secondary'}>
                            {key.is_active ? 'Actief' : 'Inactief'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <code className="text-sm bg-muted px-2 py-1 rounded">
                            {maskApiKey(key.api_key)}
                          </code>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="h-6 w-6"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyToClipboard(key.api_key, `key-${key.id}`);
                            }}
                          >
                            {copiedField === `key-${key.id}` ? (
                              <Check className="h-3 w-3" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <Shield className="h-3 w-3 inline mr-1" />
                          {getPermissionSummary(key.permissions)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <ChevronDown className={`h-4 w-4 transition-transform ${expandedKeys.has(key.id) ? 'rotate-180' : ''}`} />
                          </Button>
                        </CollapsibleTrigger>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            openPermissionsDialog(key);
                          }}
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Switch
                          checked={key.is_active}
                          onCheckedChange={(checked) => toggleApiKey(key.id, checked)}
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteKeyId(key.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <CollapsibleContent>
                      <div className="px-4 pb-4 pt-0 border-t">
                        <div className="grid grid-cols-2 gap-4 pt-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Aangemaakt:</span>
                            <span className="ml-2">{format(new Date(key.created_at), 'dd MMM yyyy HH:mm', { locale: nl })}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Laatst gebruikt:</span>
                            <span className="ml-2">
                              {key.last_used_at 
                                ? format(new Date(key.last_used_at), 'dd MMM yyyy HH:mm', { locale: nl })
                                : 'Nooit'
                              }
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Requests:</span>
                            <span className="ml-2">{key.request_count || 0}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Rate limit:</span>
                            <span className="ml-2">{key.permissions?.rateLimit || 1000}/uur</span>
                          </div>
                        </div>
                        <div className="mt-4">
                          <span className="text-sm text-muted-foreground">Endpoint rechten:</span>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {Object.entries(key.permissions?.endpoints || defaultPermissions.endpoints).map(([endpoint, perm]) => {
                              const hasRead = perm.read;
                              const hasWrite = perm.write;
                              const isActive = hasRead || hasWrite;
                              const permLabel = hasRead && hasWrite ? 'R/W' : hasRead ? 'R' : hasWrite ? 'W' : '-';
                              
                              return (
                                <Badge 
                                  key={endpoint} 
                                  variant={isActive ? 'default' : 'outline'}
                                  className={!isActive ? 'opacity-50' : ''}
                                >
                                  {endpointLabels[endpoint]?.label || endpoint}
                                  {isActive && <span className="ml-1 opacity-75">({permLabel})</span>}
                                </Badge>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nieuwe API Key Aanmaken</DialogTitle>
            <DialogDescription>
              Geef een beschrijvende naam en stel de rechten in voor deze API key
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label htmlFor="keyName">Naam</Label>
              <Input
                id="keyName"
                placeholder="bijv. Productie Server, Test Omgeving"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
              />
            </div>
            <PermissionsEditor 
              permissions={newKeyPermissions} 
              onChange={setNewKeyPermissions} 
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annuleren
            </Button>
            <Button onClick={createApiKey} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Aanmaken...
                </>
              ) : (
                'Aanmaken'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Rechten Bewerken</DialogTitle>
            <DialogDescription>
              Wijzig de rechten voor "{editingKey?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 max-h-[60vh] overflow-y-auto">
            <PermissionsEditor 
              permissions={editingPermissions} 
              onChange={setEditingPermissions} 
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPermissionsDialog(false)}>
              Annuleren
            </Button>
            <Button onClick={updatePermissions}>
              Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show Secret Dialog */}
      <Dialog open={showSecretDialog} onOpenChange={setShowSecretDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-green-600 flex items-center gap-2">
              <Check className="h-5 w-5" />
              API Key Succesvol Aangemaakt
            </DialogTitle>
            <DialogDescription>
              Kopieer en bewaar deze gegevens veilig. De API Secret wordt slechts één keer getoond!
            </DialogDescription>
          </DialogHeader>
          
          {newKeyData && (
            <div className="space-y-4 py-4">
              <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                <p className="text-sm text-amber-800 dark:text-amber-200 font-medium">
                  ⚠️ Bewaar deze gegevens nu! De API Secret kan niet opnieuw worden bekeken.
                </p>
              </div>

              <div className="space-y-2">
                <Label>API Key</Label>
                <div className="flex items-center gap-2">
                  <Input 
                    readOnly 
                    value={newKeyData.api_key} 
                    className="font-mono text-sm"
                  />
                  <Button 
                    size="icon" 
                    variant="outline"
                    onClick={() => copyToClipboard(newKeyData.api_key, 'new-key')}
                  >
                    {copiedField === 'new-key' ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>API Secret</Label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Input 
                      readOnly 
                      type={showSecret ? 'text' : 'password'}
                      value={newKeyData.api_secret} 
                      className="font-mono text-sm pr-10"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowSecret(!showSecret)}
                    >
                      {showSecret ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Button 
                    size="icon" 
                    variant="outline"
                    onClick={() => copyToClipboard(newKeyData.api_secret, 'new-secret')}
                  >
                    {copiedField === 'new-secret' ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="bg-muted p-4 rounded-lg">
                <p className="text-sm font-medium mb-2">Gebruik in je requests:</p>
                <code className="text-xs block">
                  X-API-Key: {newKeyData.api_key}<br/>
                  X-API-Secret: {showSecret ? newKeyData.api_secret : '••••••••••••••••'}
                </code>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => {
              setShowSecretDialog(false);
              setNewKeyData(null);
              setShowSecret(false);
            }}>
              Ik heb de gegevens opgeslagen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteKeyId} onOpenChange={() => setDeleteKeyId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>API Key Verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze API key wilt verwijderen? 
              Alle applicaties die deze key gebruiken zullen geen toegang meer hebben.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteApiKey}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
